import { useSelector } from '@xstate/react';
import { Button, IconStartAdornment } from 'components/Button';
import { ScheduleState } from 'domains/signupv4/schedule/scheduleMachineV4';
import { ScheduleV4EnterAppointmentType } from 'domains/signupv4/Steps/Schedule/EnterAppointmentType';
import { ScheduleV4EnterConsultationDate } from 'domains/signupv4/Steps/Schedule/EnterConsultationDate';
import { ScheduleV4EnterEvaluationDate } from 'domains/signupv4/Steps/Schedule/EnterEvaluationDate';
import { ScheduleV4ConfirmConsultationDate } from 'domains/signupv4/Steps/Schedule/ConfirmConsultationDate';
import { ScheduleV4ConfirmEvaluationDate } from 'domains/signupv4/Steps/Schedule/ConfirmEvaluationDate';
import { ScheduleV4ContactSupport } from 'domains/signupv4/Steps/Schedule/ContactSupport';
import { ScheduleV4ContinueToConsultation } from 'domains/signupv4/Steps/Schedule/ContinueToConsultation';
import { ScheduleV4ErrorStep } from 'domains/signupv4/Steps/Shared/ErrorStep';
import { ScheduleV4LoadingStep } from 'domains/signupv4/Steps/Shared/LoadingStep';
import { SignupContextV4, withSignupMachineStateV4 } from 'domains/signupv4/Wrapper/context';
import { ScheduleContextV4 } from 'domains/signupv4/schedule/context';
import { scheduleMachineV4 } from 'domains/signupv4/schedule/scheduleMachineV4';
import { signupV4StateChangeListener } from 'domains/signupv4/tracking/mixpanelTracking';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { useActorUtilities } from 'utils/xstate/context';
import { ActorRefFrom } from 'xstate';

const EnteringSchedule: React.FC = () => {
  const scheduleActor = SignupContextV4.useSelector(
    state => state.children.runScheduleMachine as ActorRefFrom<typeof scheduleMachineV4>,
  );

  // Subscribe to log state changes with mixpanel
  useEffectOnce(() => {
    // Needed to catch the first event as soon as the machine starts
    const snapshot = scheduleActor.getSnapshot();
    signupV4StateChangeListener(snapshot.value as ScheduleState);

    return scheduleActor.subscribe(({ value }) => {
      signupV4StateChangeListener(value as ScheduleState);
    }).unsubscribe;
  });

  const hasBack = useSelector(scheduleActor, state => state.can({ type: 'back' }));
  const { send } = scheduleActor;
  const { t } = useTranslation();

  return (
    <ScheduleContextV4.Provider value={useActorUtilities(scheduleActor)}>
      {hasBack ? (
        <Button
          variant="link"
          startAdornment={<IconStartAdornment name="AngleLeft" />}
          className="mb-24"
          onClick={() => send({ type: 'back' })}
        >
          {t('screener.backButton')}
        </Button>
      ) : (
        <div className="mb-64"></div>
      )}
      <ScheduleV4EnterAppointmentType />
      <ScheduleV4ContinueToConsultation />

      <ScheduleV4EnterConsultationDate />
      <ScheduleV4EnterEvaluationDate />

      <ScheduleV4ConfirmConsultationDate />
      <ScheduleV4ConfirmEvaluationDate />

      <ScheduleV4ContactSupport />

      <ScheduleV4LoadingStep />
      <ScheduleV4ErrorStep />
    </ScheduleContextV4.Provider>
  );
};

export const SignupV4EnteringSchedule = withSignupMachineStateV4(EnteringSchedule, 'enteringSchedule');
